import useIsDesktop from "@hooks/useIsDesktop"
import useNavigationLinks from "@hooks/useNavigationLinks"
import useRouter from "@hooks/useRouter"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import filterUserLinks from "@utils/filterUserLinks"

import { AccessCheck } from "@particles"

import { NavTopBar } from "@organisms"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import { LIST_SEARCH_PARAM_TYPE } from "@constants/searchParams"
import { PRICEBOOK_LIST_CONTROLS_VIEW } from "@constants/storage"

import PricebookCardView from "./views/PricebookCardView/PricebookCardView"
import PricebookTableView from "./views/PricebookTableView/PricebookTableView"

export const CREATE_ROUTE_MAP: Record<PricebookType, string> = {
    service: PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
    part: PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
    other: PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
    discount: PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
    tax: PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
}

export const PRICEBOOK_TYPE_NAME_MAP: Record<PricebookTypeName, PricebookType> = {
    Services: "service",
    "Parts & Materials": "part",
    Miscellaneous: "other",
    Discounts: "discount",
    "Tax Rates": "tax",
}

interface PricebookListContentProps {
    pricebookTypeName: PricebookTypeName
    pricebookType: PricebookType
}

function PricebookListContent({ pricebookTypeName, pricebookType }: PricebookListContentProps) {
    const isDesktop = useIsDesktop()

    const { user } = useUser()
    const { push } = useRouter()
    const { pricebook: pricebookLink } = useNavigationLinks()

    const sublinks = filterUserLinks(pricebookLink.subLinks, user)

    const [createRoute] = useSlugExtractor([CREATE_ROUTE_MAP[pricebookType]])

    return (
        <>
            <ViewTypesProvider
                viewTypeKey={PRICEBOOK_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "card"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle
                                        label={pricebookTypeName ?? ""}
                                        icon={objectMap.pricebook_item.iconActive}
                                        onNavigationItemSelect={(item) => {
                                            push(item.value)
                                        }}
                                        navigationItems={
                                            sublinks.length > 1
                                                ? sublinks.map((item) => ({
                                                      label: item.label,
                                                      value: item.path,
                                                      isActive: pricebookTypeName === item.label,
                                                  }))
                                                : undefined
                                        }
                                    />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            pricebook_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                pricebook_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Pricebook" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            pricebook_create_permission: PERMISSION_LEVEL.FULL,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        {activeViewType === "card" ? (
                            <PricebookCardView pricebookTypeName={pricebookTypeName} />
                        ) : (
                            <PricebookTableView pricebookTypeName={pricebookTypeName} />
                        )}
                    </>
                )}
            </ViewTypesProvider>
        </>
    )
}

export default function PricebookList() {
    let pricebookTypeName: PricebookTypeName = new URLSearchParams(window.location.search).get(
        LIST_SEARCH_PARAM_TYPE,
    ) as PricebookTypeName

    if (!pricebookTypeName) {
        pricebookTypeName = "Services"
        const url = new URL(window.location.href)
        url.searchParams.set(LIST_SEARCH_PARAM_TYPE, "Services")
        window.history.replaceState({}, "", url)
    }

    const pricebookType = PRICEBOOK_TYPE_NAME_MAP[pricebookTypeName]

    return (
        <AppFrame.Root collaborationRoomName={`pricebook-list-${pricebookType}`}>
            <PricebookListContent pricebookTypeName={pricebookTypeName} pricebookType={pricebookType} />
        </AppFrame.Root>
    )
}
